import { useMemo, useState } from 'react';
import { todayIso, toIsoDate } from '../utils/dateUtils';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetReports({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const today = todayIso();
  const plus60 = toIsoDate(new Date(Date.now() + (60 * 86400000)));
  const [filters, setFilters] = useState({ from: today, to: plus60 });
  const [kpi, setKpi] = useState({ tentative_count: 0, confirmed_count: 0, overdue_advance_count: 0 });
  const [heatmap, setHeatmap] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const load = async () => {
    if (!chainId || !propertyId) {
      setError('Select property first');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams({ from: filters.from, to: filters.to });
      const res = await fetch(`/api/v1/banquet/reports/kpi?${q.toString()}`, { headers });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Failed to load Banquet KPI report');
      setKpi({
        tentative_count: Number(data?.kpi?.tentative_count || 0),
        confirmed_count: Number(data?.kpi?.confirmed_count || 0),
        overdue_advance_count: Number(data?.kpi?.overdue_advance_count || 0)
      });
      setHeatmap(Array.isArray(data?.daily_revenue_heatmap) ? data.daily_revenue_heatmap : []);
    } catch (err) {
      setError(err?.message || 'Failed to load Banquet KPI report');
      setKpi({ tentative_count: 0, confirmed_count: 0, overdue_advance_count: 0 });
      setHeatmap([]);
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = { border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: '8px', padding: '0.8rem' };
  const kpiBars = [
    { key: 'tentative', label: 'Tentative', value: Number(kpi.tentative_count || 0), color: '#f59e0b' },
    { key: 'confirmed', label: 'Confirmed', value: Number(kpi.confirmed_count || 0), color: 'var(--theme-success)' },
    { key: 'overdue', label: 'Overdue', value: Number(kpi.overdue_advance_count || 0), color: 'var(--theme-danger)' }
  ];
  const maxKpiValue = Math.max(...kpiBars.map((b) => b.value), 1);
  const trendRows = heatmap.slice(-14);
  const maxRevenue = Math.max(...trendRows.map((r) => Number(r.revenue || 0)), 1);
  const chartW = 720;
  const chartH = 220;
  const pad = 36;
  const innerW = chartW - (pad * 2);
  const innerH = chartH - (pad * 2);
  const trendPoints = trendRows.map((r, idx) => {
    const x = pad + (trendRows.length <= 1 ? innerW / 2 : (idx * innerW) / (trendRows.length - 1));
    const y = chartH - pad - ((Number(r.revenue || 0) / maxRevenue) * innerH);
    return { x, y, label: String(r.event_date || '').slice(5, 10), value: Number(r.revenue || 0) };
  });
  const trendPath = trendPoints.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Dashboard & Reports</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
        <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        <button onClick={load}>{loading ? 'Loading...' : 'Load KPIs'}</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '0.8rem', marginBottom: '1rem' }}>
        <div className="card card-body">
          <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>KPI Comparison (Bar)</h4>
          <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', height: '220px', background: 'var(--theme-bg-light)', borderRadius: '6px' }}>
            <line x1={pad} y1={chartH - pad} x2={chartW - pad} y2={chartH - pad} stroke="var(--theme-text-muted)" strokeWidth="1" />
            {kpiBars.map((bar, idx) => {
              const bw = 90;
              const gap = 55;
              const x = pad + 30 + (idx * (bw + gap));
              const bh = (bar.value / maxKpiValue) * innerH;
              const y = chartH - pad - bh;
              return (
                <g key={bar.key}>
                  <rect x={x} y={y} width={bw} height={bh} fill={bar.color} rx="5" />
                  <text x={x + (bw / 2)} y={chartH - 12} textAnchor="middle" fontSize="12" fill="#334155">{bar.label}</text>
                  <text x={x + (bw / 2)} y={Math.max(y - 8, 14)} textAnchor="middle" fontSize="12" fill="#0f172a">{bar.value}</text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="card card-body">
          <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Daily Revenue Trend (Line)</h4>
          <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', height: '220px', background: 'var(--theme-bg-light)', borderRadius: '6px' }}>
            <line x1={pad} y1={chartH - pad} x2={chartW - pad} y2={chartH - pad} stroke="var(--theme-text-muted)" strokeWidth="1" />
            <line x1={pad} y1={pad} x2={pad} y2={chartH - pad} stroke="var(--theme-text-muted)" strokeWidth="1" />
            {trendPoints.length > 1 && <path d={trendPath} fill="none" stroke="#2563eb" strokeWidth="2.5" />}
            {trendPoints.map((p, idx) => (
              <g key={`${p.label}-${idx}`}>
                <circle cx={p.x} cy={p.y} r="3.5" fill="#1d4ed8" />
                <text x={p.x} y={chartH - 12} textAnchor="middle" fontSize="10" fill="#334155">{p.label}</text>
              </g>
            ))}
            {trendPoints.length === 0 && <text x={chartW / 2} y={chartH / 2} textAnchor="middle" fill="var(--theme-text-secondary)">No trend data</text>}
          </svg>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem', marginBottom: '1rem' }}>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.85rem', color: '#334155' }}>Tentative Bookings</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#92400e' }}>{kpi.tentative_count}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.85rem', color: '#334155' }}>Confirmed Bookings</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#166534' }}>{kpi.confirmed_count}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.85rem', color: '#334155' }}>Overdue Advances</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#b91c1c' }}>{kpi.overdue_advance_count}</div>
        </div>
      </div>

      <div className="card card-body">
        <h4 style={{ marginTop: 0 }}>Daily Revenue Heatmap (Data)</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Event Date</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {heatmap.map((h) => (
                <tr key={h.event_date}>
                  <td>{String(h.event_date || '').slice(0, 10)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(h.revenue || 0).toFixed(2)}</td>
                </tr>
              ))}
              {!heatmap.length ? <tr><td colSpan={2}>No data available for selected period.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default BanquetReports;
